import { GoogleMap, Polyline, Marker, Circle, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { useTheme } from 'next-themes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { mockRoutePath } from '../../lib/mockData';
import { GOOGLE_MAPS_LIBRARIES, useGoogleMapsRuntimeError } from '../../lib/google-maps';

const darkStyles = [
  { elementType: 'geometry', stylers: [{ color: '#0a0a0a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0a0a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#737373' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#262626' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#050505' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#262626' }] },
];

const lightStyles = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

/**
 * MapView renders a Google Map with:
 *  - Optional polyline path (defaults to mock route in Lagos)
 *  - Optional `startPoint` / `finishPoint` markers (event start/finish)
 *  - Optional "current GPS position" indicator (showCurrent + currentIndex)
 *  - A native-style "My Location" pulsing blue dot when `showUserLocation` is true,
 *    which calls navigator.geolocation.getCurrentPosition on mount for fast centering.
 */
export function MapView({
  height = 280,
  path = mockRoutePath,
  showCurrent = false,
  currentIndex = 0,
  className = '',
  startPoint = null,
  finishPoint = null,
  showUserLocation = false,
  userAvatarUrl = null,
  checkpoints = null,
  passedCheckpointIndices = null,
  // Live-follow camera: when set, the camera smooth-pans to this {lat,lng}
  // on every change (debounced to the panning speed, no snap). Use this on
  // the Live Race screen to keep the runner centered as they move.
  followPosition = null,
}) {
  const { theme } = useTheme();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey || '', libraries: GOOGLE_MAPS_LIBRARIES });
  const [userLoc, setUserLoc] = useState(null);
  const gmErrored = useGoogleMapsRuntimeError(isLoaded);
  const mapRef = useRef(null);

  // Get the user's location fast for centering (one-shot, low-accuracy is fine
  // to seed the camera — useGpsTracker still provides the live high-accuracy fix).
  useEffect(() => {
    if (!showUserLocation || typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 }
    );
  }, [showUserLocation]);

  // Follow-camera: when `followPosition` changes we pan the map smoothly to
  // the new fix. Using panTo imperatively (vs mutating the controlled
  // `center` prop) avoids the hard snap you get when react re-renders the
  // GoogleMap with a new center every tick.
  useEffect(() => {
    if (!followPosition || !mapRef.current) return;
    try {
      const target = { lat: Number(followPosition.lat), lng: Number(followPosition.lng) };
      if (Number.isFinite(target.lat) && Number.isFinite(target.lng)) {
        mapRef.current.panTo(target);
      }
    } catch { /* noop — map may not be fully initialised */ }
  }, [followPosition?.lat, followPosition?.lng]);

  // Camera priority: live GPS → user location → start/finish point → mid-path → Lagos fallback
  const center = useMemo(() => {
    if (showCurrent && path?.[currentIndex]) return path[currentIndex];
    if (userLoc) return userLoc;
    if (startPoint) return startPoint;
    if (finishPoint) return finishPoint;
    if (path?.length) return path[Math.floor(path.length / 2)];
    return { lat: 6.5244, lng: 3.3792 }; // Lagos
  }, [path, currentIndex, showCurrent, userLoc, startPoint, finishPoint]);

  const styles = theme === 'dark' ? darkStyles : lightStyles;

  if (!apiKey || !isLoaded || loadError || gmErrored) {
    return (
      <MapFallback
        height={height}
        path={path}
        error={loadError || gmErrored ? 'Google Maps unavailable. Please try again later.' : null}
        showCurrent={showCurrent}
        currentIndex={currentIndex}
        className={className}
        startPoint={startPoint}
        finishPoint={finishPoint}
      />
    );
  }

  const hasExplicitPoints = Boolean(startPoint || finishPoint);
  const hasGpsPath = Array.isArray(path) && path.length > 1 && path !== mockRoutePath;

  return (
    <div
      className={`relative overflow-hidden rounded-md border border-border ${className}`}
      style={{ height }}
      data-testid="map-view"
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={hasGpsPath || showCurrent ? 16 : 14}
        onLoad={(m) => { mapRef.current = m; }}
        onUnmount={() => { mapRef.current = null; }}
        options={{
          styles,
          disableDefaultUI: true,
          zoomControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          rotateControl: false,
          scaleControl: false,
          panControl: false,
          keyboardShortcuts: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        }}
      >
        {/* Recorded route (live race / personal run) */}
        {hasGpsPath && (
          <Polyline
            path={path}
            options={{ strokeColor: '#DC2626', strokeWeight: 4, strokeOpacity: 1 }}
          />
        )}

        {/* Checkpoint layer — green pin & connecting segment for passed
            checkpoints, red pin for unpassed. Segments join the previous
            passed checkpoint (or event start) to this one. */}
        {Array.isArray(checkpoints) && checkpoints.length > 0 && (() => {
          const passedSet = passedCheckpointIndices instanceof Set
            ? passedCheckpointIndices
            : new Set(Array.isArray(passedCheckpointIndices) ? passedCheckpointIndices : []);
          const segAnchor = startPoint || (Array.isArray(path) && path.length ? path[0] : null);
          const segments = [];
          let prev = segAnchor;
          for (let i = 0; i < checkpoints.length; i++) {
            const cp = checkpoints[i];
            if (typeof cp?.lat !== 'number' || typeof cp?.lng !== 'number') { continue; }
            if (prev && passedSet.has(i)) {
              segments.push({ from: prev, to: { lat: cp.lat, lng: cp.lng }, key: `seg-${i}` });
            }
            prev = { lat: cp.lat, lng: cp.lng };
          }
          return (
            <>
              {segments.map((s) => (
                <Polyline
                  key={s.key}
                  path={[s.from, s.to]}
                  options={{ strokeColor: '#16a34a', strokeWeight: 5, strokeOpacity: 0.9, zIndex: 1 }}
                />
              ))}
              {checkpoints.map((cp, i) => {
                if (typeof cp?.lat !== 'number' || typeof cp?.lng !== 'number') return null;
                const passedThis = passedSet.has(i);
                return (
                  <Marker
                    key={`cp-${i}`}
                    position={{ lat: cp.lat, lng: cp.lng }}
                    label={{ text: String(i + 1), color: '#fff', fontSize: '11px', fontWeight: 'bold' }}
                    icon={{
                      path: 'M 0,0 m -10,0 a 10,10 0 1,0 20,0 a 10,10 0 1,0 -20,0',
                      fillColor: passedThis ? '#16a34a' : '#0F2A8E',
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2,
                      scale: 1,
                      labelOrigin: { x: 0, y: 0 },
                    }}
                    zIndex={5}
                  />
                );
              })}
            </>
          );
        })()}

        {/* Event start / finish markers (from organizer's picker) */}
        {startPoint && (
          <Marker
            position={startPoint}
            label={{ text: 'S', color: '#fff', fontWeight: 'bold' }}
            icon={{
              path: 'M 0,0 m -12,0 a 12,12 0 1,0 24,0 a 12,12 0 1,0 -24,0',
              fillColor: '#16a34a',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
              scale: 1,
              labelOrigin: { x: 0, y: 0 },
            }}
          />
        )}
        {finishPoint && (
          <Marker
            position={finishPoint}
            label={{ text: 'F', color: '#fff', fontWeight: 'bold' }}
            icon={{
              path: 'M 0,0 m -12,0 a 12,12 0 1,0 24,0 a 12,12 0 1,0 -24,0',
              fillColor: '#E63946',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
              scale: 1,
              labelOrigin: { x: 0, y: 0 },
            }}
          />
        )}

        {/* If no explicit points: mark the start/end of the recorded path */}
        {!hasExplicitPoints && hasGpsPath && (
          <>
            <Marker position={path[0]} label={{ text: 'S', color: '#fff' }} />
            <Marker position={path[path.length - 1]} label={{ text: 'F', color: '#fff' }} />
          </>
        )}

        {/* Live cursor while running */}
        {showCurrent && path[currentIndex] && (
          <Marker
            position={path[currentIndex]}
            icon={{
              path: 0,
              scale: 8,
              fillColor: '#DC2626',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
          />
        )}

        {/* Native-style "you are here" pulsing dot — uses the user's avatar
            as the marker icon when one is provided, otherwise a blue dot. */}
        {showUserLocation && userLoc && (
          <>
            <Circle
              center={userLoc}
              radius={30}
              options={{
                fillColor: '#1D4ED8',
                fillOpacity: 0.18,
                strokeOpacity: 0,
                clickable: false,
              }}
            />
            <Marker
              position={userLoc}
              icon={!userAvatarUrl ? {
                path: 0,
                scale: 6,
                fillColor: '#1D4ED8',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2.5,
              } : { path: 'M 0,0 m -0.01,0 a 0.01,0.01 0 1,0 0.02,0 a 0.01,0.01 0 1,0 -0.02,0', scale: 1, fillOpacity: 0, strokeOpacity: 0 }}
              zIndex={999}
            />
            {userAvatarUrl && (
              <OverlayView
                position={userLoc}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
              >
                <div
                  data-testid="user-avatar-marker"
                  className="rounded-full border-2 border-white shadow-md bg-cover bg-center pointer-events-none"
                  style={{
                    width: 24,
                    height: 24,
                    backgroundImage: `url(${userAvatarUrl})`,
                  }}
                />
              </OverlayView>
            )}
          </>
        )}
      </GoogleMap>
    </div>
  );
}

function MapFallback({ height, path, showCurrent, currentIndex, className, error }) {
  if (!path?.length) {
    return (
      <div
        className={`relative overflow-hidden rounded-md border border-border bg-muted ${className}`}
        style={{ height }}
        data-testid={error ? 'map-fallback-error' : 'map-fallback-empty'}
      >
        <div className="absolute inset-0 rivl-grid-bg opacity-30" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {error ? 'Map unavailable' : 'Locating you…'}
          </div>
          {error && (
            <div className="text-[11px] text-muted-foreground/80 max-w-[240px]">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }  const lats = path.map((p) => p.lat);
  const lngs = path.map((p) => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const pad = 0.001;
  const W = 600, H = 320;
  const project = (p) => {
    const x = ((p.lng - minLng + pad) / (maxLng - minLng + pad * 2)) * W;
    const y = H - ((p.lat - minLat + pad) / (maxLat - minLat + pad * 2)) * H;
    return [x, y];
  };
  const points = path.map(project);
  const d = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');

  return (
    <div
      className={`relative overflow-hidden rounded-md border border-border bg-muted ${className}`}
      style={{ height }}
      data-testid="map-fallback"
    >
      <div className="absolute inset-0 rivl-grid-bg opacity-30" />
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <path d={d} fill="none" stroke="#DC2626" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {points[0] && <circle cx={points[0][0]} cy={points[0][1]} r="6" fill="#0F2A8E" stroke="#fff" strokeWidth="2" />}
        {points[points.length - 1] && (
          <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="6" fill="#DC2626" stroke="#fff" strokeWidth="2" />
        )}
        {showCurrent && points[currentIndex] && (
          <g>
            <circle cx={points[currentIndex][0]} cy={points[currentIndex][1]} r="14" fill="#DC2626" opacity="0.25">
              <animate attributeName="r" values="8;18;8" dur="1.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="1.4s" repeatCount="indefinite" />
            </circle>
            <circle cx={points[currentIndex][0]} cy={points[currentIndex][1]} r="6" fill="#DC2626" stroke="#fff" strokeWidth="2" />
          </g>
        )}
      </svg>
      <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-widest text-muted-foreground bg-background/70 px-2 py-1 rounded">
        {error ? 'Map unavailable' : 'Map preview · add Google Maps key for live map'}
      </div>
    </div>
  );
}
